import { useEffect, useState } from "react";
import { fmtMYTDate } from "@/lib/myt";
import { AlertTriangle, Check, Copy, Eye, KeyRound, Pencil, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { MemberDetailModal } from "@/components/MemberDetailModal";
import { AddProgressModal } from "@/components/admin/AddProgressModal";

const EMPTY_FORM = { username: "", full_name: "", email: "", password: "", referral_code: "", assign_as_local_depositor: false };

export default function AdminIBs() {
  const [ibs, setIbs] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [editModal, setEditModal] = useState(null); // {id, full_name, email}
  const [codeModal, setCodeModal] = useState(null); // {id, referral_code}
  const [purgeModal, setPurgeModal] = useState(null); // {ib, preview, confirm, busy, result, error}
  const [addProgressMember, setAddProgressMember] = useState(null);

  const openPurge = async (ib) => {
    setPurgeModal({ ib, preview: null, confirm: "", busy: false, result: null, error: "" });
    try {
      const { data } = await api.post(`/admin/ibs/${ib.id}/purge-preview`);
      setPurgeModal((m) => ({ ...m, preview: data }));
    } catch (err) {
      setPurgeModal((m) => ({ ...m, error: formatApiError(err) }));
    }
  };

  const executePurge = async () => {
    setPurgeModal((m) => ({ ...m, busy: true, error: "" }));
    try {
      const { data } = await api.post(`/admin/ibs/${purgeModal.ib.id}/purge`, { confirmation: purgeModal.confirm });
      setPurgeModal((m) => ({ ...m, busy: false, result: data }));
      api.get("/admin/ibs").then((res) => setIbs(res.data)).catch(() => {});
    } catch (err) {
      setPurgeModal((m) => ({ ...m, busy: false, error: formatApiError(err) }));
    }
  };

  const [modalError, setModalError] = useState("");
  const [modalBusy, setModalBusy] = useState(false);
  const [membersModal, setMembersModal] = useState(null); // {ib, members|null}
  const [memberDetailId, setMemberDetailId] = useState(null);
  const [memberFilter, setMemberFilter] = useState("all"); // all | not_checked
  const [membersPage, setMembersPage] = useState(1);
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [savingRemarkId, setSavingRemarkId] = useState(null);

  const copyEmail = async (m) => {
    if (!m.email) return;
    try {
      await navigator.clipboard.writeText(m.email);
      setCopiedEmailId(m.id);
      toast.success("Email copied");
      setTimeout(() => setCopiedEmailId((c) => (c === m.id ? null : c)), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const toggleEmailFound = async (m) => {
    const next = !m.email_found;
    setMembersModal((mm) => ({ ...mm, members: mm.members.map((x) => (x.id === m.id ? { ...x, email_found: next } : x)) }));
    try {
      const { data } = await api.patch(`/admin/ibs/${membersModal.ib.id}/members/${m.id}/email-check`, { email_found: next });
      setMembersModal((mm) => ({ ...mm, members: mm.members.map((x) => (x.id === m.id ? { ...x, email_found: data.email_found, email_found_by: data.email_found_by, email_found_at: data.email_found_at } : x)) }));
      toast.success(next ? "Marked as Email Found" : "Unmarked");
    } catch (err) {
      setMembersModal((mm) => ({ ...mm, members: mm.members.map((x) => (x.id === m.id ? { ...x, email_found: !next } : x)) }));
      toast.error(formatApiError(err));
    }
  };

  const saveRemark = async (m) => {
    const value = remarkDrafts[m.id] ?? "";
    setSavingRemarkId(m.id);
    try {
      const { data } = await api.patch(`/admin/ibs/${membersModal.ib.id}/members/${m.id}/remark`, { remark: value });
      setMembersModal((mm) => ({ ...mm, members: mm.members.map((x) => (x.id === m.id ? { ...x, remark: data.remark, remark_updated_at: data.remark_updated_at } : x)) }));
      setRemarkDrafts((d) => ({ ...d, [m.id]: data.remark }));
      toast.success("Remark saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingRemarkId(null);
    }
  };

  const load = () => {
    api.get("/admin/ibs").then((res) => setIbs(res.data)).catch(() => setIbs([]));
  };

  useEffect(load, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form, referral_code: form.referral_code || null };
      const { data } = await api.post("/admin/ibs", payload);
      toast.success(`IB created — referral code ${data.referral_code}`);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async (ib) => {
    try {
      await navigator.clipboard.writeText(ib.referral_code);
      setCopiedId(ib.id);
      toast.success("Referral code copied");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const openMembers = async (ib) => {
    setMembersModal({ ib, members: null });
    setMemberFilter("all");
    setMembersPage(1);
    setCopiedEmailId(null);
    try {
      const { data } = await api.get(`/admin/ibs/${ib.id}/members`);
      setMembersModal({ ib, members: data });
      setRemarkDrafts(Object.fromEntries((data || []).map((m) => [m.id, m.remark || ""])));
    } catch (err) {
      toast.error(formatApiError(err));
      setMembersModal(null);
    }
  };

  const toggleStatus = async (ib) => {
    const next = ib.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/admin/users/${ib.id}/status`, { status: next });
      setIbs((prev) => prev.map((x) => (x.id === ib.id ? { ...x, status: next } : x)));
      toast.success(`${ib.full_name} is now ${next}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveEdit = async () => {
    setModalBusy(true);
    setModalError("");
    try {
      await api.put(`/admin/ibs/${editModal.id}`, {
        full_name: editModal.full_name,
        email: editModal.email,
      });
      toast.success("IB account updated");
      setEditModal(null);
      load();
    } catch (err) {
      setModalError(formatApiError(err));
    } finally {
      setModalBusy(false);
    }
  };

  const saveCode = async () => {
    setModalBusy(true);
    setModalError("");
    try {
      const { data } = await api.put(`/admin/ibs/${codeModal.id}/referral-code`, {
        referral_code: codeModal.referral_code || null,
      });
      toast.success(`Referral code set to ${data.referral_code}`);
      setCodeModal(null);
      load();
    } catch (err) {
      setModalError(formatApiError(err));
    } finally {
      setModalBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-ibs-page">
      <div>
        <p className="eyebrow-label mb-2">Management</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">IB Management</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          Create Introducing Broker accounts. Each IB receives a unique referral code used for client registration.
        </p>
      </div>

      <div className="card-lux p-8">
        <p className="eyebrow-label mb-6">Create New IB</p>
        <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Username</label>
            <input
              type="text"
              value={form.username}
              onChange={handleChange("username")}
              className="input-lux font-mono"
              placeholder="e.g. ib_sarah"
              required
              data-testid="create-ib-username-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={handleChange("full_name")}
              className="input-lux"
              placeholder="IB full name"
              required
              data-testid="create-ib-fullname-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              className="input-lux"
              placeholder="ib@email.com"
              required
              data-testid="create-ib-email-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              className="input-lux"
              placeholder="Min. 8 characters"
              required
              minLength={8}
              data-testid="create-ib-password-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
              Referral Code <span className="text-[#64748B]">(optional — auto-generated if empty)</span>
            </label>
            <input
              type="text"
              value={form.referral_code}
              onChange={(e) => setForm((f) => ({ ...f, referral_code: e.target.value.toUpperCase() }))}
              className="input-lux font-mono tracking-widest uppercase"
              placeholder="e.g. SARAH2026"
              data-testid="create-ib-referral-code-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.assign_as_local_depositor}
                onChange={(e) => setForm((f) => ({ ...f, assign_as_local_depositor: e.target.checked }))}
                className="h-4 w-4 accent-[#D4AF37]"
                data-testid="create-ib-local-depositor-checkbox"
              />
              <span className="text-sm text-[#E5E7EB]">
                Assign as Local Depositor <span className="text-[#64748B]">(optional — only allowed if no active Local Depositor exists)</span>
              </span>
            </label>
          </div>
          {error && (
            <p className="sm:col-span-2 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="create-ib-error-message">
              {error}
            </p>
          )}
          <div className="sm:col-span-2">
            <button type="submit" disabled={loading} className="btn-gold px-8 py-3 text-sm disabled:opacity-60" data-testid="create-ib-submit-button">
              {loading ? "Creating..." : "Create IB Account"}
            </button>
          </div>
        </form>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-ibs-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Username</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Full Name</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Referral Code</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Clients</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden sm:table-cell">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Created</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {ibs === null ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#64748B]">Loading IBs...</td></tr>
              ) : ibs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-ibs-empty">
                    No IB accounts yet. Create the first one above.
                  </td>
                </tr>
              ) : (
                ibs.map((ib) => (
                  <tr key={ib.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-ib-row-${ib.username}`}>
                    <td className="px-6 py-4 font-mono text-white">{ib.username}</td>
                    <td className="px-6 py-4 text-white">{ib.full_name}</td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[#D4AF37] tracking-widest" data-testid={`admin-ib-code-${ib.username}`}>
                          {ib.referral_code}
                        </span>
                        <button
                          onClick={() => copyCode(ib)}
                          className="text-[#64748B] hover:text-[#D4AF37] transition-colors"
                          data-testid={`admin-ib-copy-${ib.username}`}
                          aria-label="Copy referral code"
                        >
                          {copiedId === ib.id ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono text-white">{ib.client_count}</td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        ib.status === "active"
                          ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30"
                          : "text-red-400 bg-red-400/10 border-red-400/30"
                      }`}>
                        {ib.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden md:table-cell">
                      {ib.created_at ? fmtMYTDate(ib.created_at) : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => openMembers(ib)}
                          className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1"
                          data-testid={`admin-ib-members-${ib.username}`}
                        >
                          <Users className="h-3 w-3" /> Members
                        </button>
                        <button
                          onClick={() => { setModalError(""); setEditModal({ id: ib.id, full_name: ib.full_name, email: ib.email }); }}
                          className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1"
                          data-testid={`admin-ib-edit-${ib.username}`}
                        >
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button
                          onClick={() => { setModalError(""); setCodeModal({ id: ib.id, referral_code: "" }); }}
                          className="text-xs text-[#94A3B8] hover:text-[#D4AF37] border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1"
                          data-testid={`admin-ib-code-btn-${ib.username}`}
                        >
                          <KeyRound className="h-3 w-3" /> Code
                        </button>
                        <button
                          onClick={() => toggleStatus(ib)}
                          className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
                            ib.status === "active"
                              ? "text-red-400 border-red-400/30 hover:bg-red-400/10"
                              : "text-emerald-400 border-emerald-400/30 hover:bg-emerald-400/10"
                          }`}
                          data-testid={`admin-ib-toggle-${ib.username}`}
                        >
                          {ib.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                        {ib.is_demo ? (
                          <span className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] border border-[#222530] rounded-lg px-2.5 py-1.5" data-testid={`admin-ib-demo-${ib.username}`}>
                            Protected
                          </span>
                        ) : (
                          <button
                            onClick={() => openPurge(ib)}
                            className="text-xs font-medium rounded-lg px-2.5 py-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1"
                            data-testid={`admin-ib-delete-${ib.username}`}
                          >
                            <AlertTriangle className="h-3 w-3" /> Delete Test IB
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {membersModal && (() => {
        const all = membersModal.members || [];
        const checkedCount = all.filter((m) => m.email_found).length;
        const filtered = memberFilter === "not_checked" ? all.filter((m) => !m.email_found) : all;
        const pageSize = 25;
        const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
        const page = Math.min(membersPage, pageCount);
        const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="ib-members-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMembersModal(null)} />
          <div className="card-lux relative w-full max-w-3xl p-5 sm:p-8 max-h-[92vh] overflow-y-auto">
            <p className="eyebrow-label mb-2">Referred Members</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-1">
              {membersModal.ib.full_name}
              <span className="font-mono text-[#D4AF37] text-sm ml-2">{membersModal.ib.referral_code}</span>
            </h3>
            {!membersModal.members ? (
              <p className="text-sm text-[#64748B] mt-4">Loading members...</p>
            ) : all.length === 0 ? (
              <p className="text-sm text-[#64748B] mt-4" data-testid="ib-members-empty">No members referred yet.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3 mb-5">
                  <p className="text-xs font-mono tracking-widest uppercase text-[#94A3B8]" data-testid="ib-members-count">
                    <span className="text-[#D4AF37]">{checkedCount}</span> / {all.length} email found
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setMemberFilter("all"); setMembersPage(1); }} data-testid="ib-members-filter-all"
                      className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${memberFilter === "all" ? "text-[#0B0C10] bg-[#D4AF37] border-[#D4AF37]" : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/40"}`}>
                      All
                    </button>
                    <button onClick={() => { setMemberFilter("not_checked"); setMembersPage(1); }} data-testid="ib-members-filter-unchecked"
                      className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-3 py-1.5 border transition-colors ${memberFilter === "not_checked" ? "text-[#0B0C10] bg-[#D4AF37] border-[#D4AF37]" : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/40"}`}>
                      Not Checked
                    </button>
                  </div>
                </div>
                <div className="space-y-3" data-testid="ib-members-list">
                  {pageItems.map((m) => (
                    <div key={m.id} data-testid={`ib-member-row-${m.username}`}
                      className={`rounded-xl border p-4 transition-colors ${m.email_found ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.04]" : "border-[#222530]"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{m.full_name}</p>
                          <p className="text-xs text-[#64748B] font-mono">@{m.username} · <span className="text-[#D4AF37]">{m.uid || "—"}</span> · {m.rank?.name || "—"}</p>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer shrink-0" data-testid={`ib-member-emailfound-label-${m.username}`}>
                          <input type="checkbox" checked={!!m.email_found} onChange={() => toggleEmailFound(m)} className="peer sr-only" data-testid={`ib-member-emailfound-${m.username}`} />
                          <span className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${m.email_found ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#475569] bg-transparent"}`}>
                            {m.email_found && <Check className="h-3.5 w-3.5 text-[#0B0C10]" />}
                          </span>
                          <span className={`text-[10px] font-mono tracking-widest uppercase ${m.email_found ? "text-[#D4AF37]" : "text-[#64748B]"}`}>Email Found</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-xs text-[#94A3B8] font-mono truncate flex-1 min-w-0" data-testid={`ib-member-email-${m.username}`}>{m.email || "—"}</span>
                        <button onClick={() => copyEmail(m)} disabled={!m.email} data-testid={`ib-member-copy-email-${m.username}`}
                          className="shrink-0 text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1 disabled:opacity-40">
                          {copiedEmailId === m.id ? <><Check className="h-3.5 w-3.5 text-emerald-400" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                        </button>
                        <button onClick={() => setAddProgressMember(m)} data-testid={`ib-member-add-progress-${m.username}`}
                          className="shrink-0 text-xs text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" /> Add Progress
                        </button>
                        <button onClick={() => setMemberDetailId(m.id)} data-testid={`ib-member-view-${m.username}`}
                          className="shrink-0 text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1">
                          <Eye className="h-3 w-3" /> View
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap sm:flex-nowrap">
                        <span className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] shrink-0">Remark</span>
                        <input value={remarkDrafts[m.id] ?? ""} onChange={(e) => setRemarkDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                          placeholder="Admin-only note…" maxLength={1000} data-testid={`ib-member-remark-input-${m.username}`}
                          className="flex-1 min-w-[140px] bg-[#0B0C10] border border-[#222530] focus:border-[#D4AF37]/50 rounded-lg px-3 py-1.5 text-xs text-white outline-none" />
                        <button onClick={() => saveRemark(m)} disabled={savingRemarkId === m.id || (remarkDrafts[m.id] ?? "") === (m.remark || "")}
                          data-testid={`ib-member-remark-save-${m.username}`}
                          className="shrink-0 text-[10px] font-mono tracking-widest uppercase rounded-lg px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40">
                          Save
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {pageCount > 1 && (
                  <div className="flex items-center justify-between mt-5">
                    <button onClick={() => setMembersPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="ib-members-prev"
                      className="text-xs text-[#94A3B8] border border-[#222530] rounded-lg px-3 py-1.5 disabled:opacity-40">Prev</button>
                    <span className="text-xs text-[#64748B] font-mono" data-testid="ib-members-page">Page {page} / {pageCount}</span>
                    <button onClick={() => setMembersPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount} data-testid="ib-members-next"
                      className="text-xs text-[#94A3B8] border border-[#222530] rounded-lg px-3 py-1.5 disabled:opacity-40">Next</button>
                  </div>
                )}
              </>
            )}
            <button onClick={() => setMembersModal(null)} className="btn-outline-gold w-full py-2.5 text-sm mt-6" data-testid="ib-members-close-button">Close</button>
          </div>
        </div>
        );
      })()}

      {memberDetailId && (
        <MemberDetailModal
          memberId={memberDetailId}
          onClose={() => setMemberDetailId(null)}
          onChanged={() => membersModal && openMembers(membersModal.ib)}
        />
      )}

      {addProgressMember && (
        <AddProgressModal
          member={addProgressMember}
          onClose={() => setAddProgressMember(null)}
          onApplied={() => membersModal && openMembers(membersModal.ib)}
        />
      )}

      {purgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="purge-ib-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => !purgeModal.busy && setPurgeModal(null)} />
          <div className="card-lux relative w-full max-w-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto border-red-500/30">
            <p className="eyebrow-label mb-2 text-red-400">Danger Zone</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-2">Delete Test IB</h3>
            <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/25 rounded-lg px-4 py-3 mb-5" data-testid="purge-warning">
              This action permanently deletes this IB and all clients registered under this IB. This cannot be undone.
            </p>
            {purgeModal.error && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3 mb-4" data-testid="purge-error">{purgeModal.error}</p>
            )}
            {purgeModal.result ? (
              <div className="space-y-4">
                <p className="text-sm text-emerald-400 bg-emerald-400/10 border border-emerald-400/25 rounded-lg px-4 py-3" data-testid="purge-success">
                  Purged {purgeModal.result.ib_username} ({purgeModal.result.referral_code}) — {purgeModal.result.clients_deleted} client(s) and all associated records removed.
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs max-h-48 overflow-y-auto" data-testid="purge-result-counts">
                  {Object.entries(purgeModal.result.deleted).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between border border-[#222530] rounded-lg px-3 py-1.5">
                      <span className="text-[#64748B] font-mono">{k}</span>
                      <span className="text-white font-mono">{v}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setPurgeModal(null)} className="btn-outline-gold w-full py-2.5 text-sm" data-testid="purge-close-button">Close</button>
              </div>
            ) : !purgeModal.preview ? (
              <p className="text-sm text-[#64748B]" data-testid="purge-preview-loading">Loading preview...</p>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-white">
                  <span className="font-semibold">{purgeModal.preview.ib.full_name}</span>{" "}
                  <span className="font-mono text-[#D4AF37] text-xs">{purgeModal.preview.ib.referral_code}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs max-h-48 overflow-y-auto" data-testid="purge-preview-counts">
                  {Object.entries(purgeModal.preview.counts).map(([k, v]) => (
                    <div key={k} className="flex justify-between border border-[#222530] rounded-lg px-3 py-1.5" data-testid={`purge-count-${k}`}>
                      <span className="text-[#64748B] font-mono">{k}</span>
                      <span className="text-white font-mono">{v}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs text-[#64748B] tracking-wide">
                    Type <span className="font-mono text-red-400">{purgeModal.preview.confirmation_phrase}</span> to confirm
                  </label>
                  <input
                    value={purgeModal.confirm}
                    onChange={(e) => setPurgeModal((m) => ({ ...m, confirm: e.target.value }))}
                    className="mt-2 w-full bg-[#0B0C10] border border-red-500/30 rounded-lg px-3 py-2.5 text-sm text-white focus:border-red-500/60 outline-none font-mono"
                    data-testid="purge-confirm-input"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setPurgeModal(null)} disabled={purgeModal.busy} className="btn-outline-gold flex-1 py-2.5 text-sm disabled:opacity-50" data-testid="purge-cancel-button">Cancel</button>
                  <button
                    onClick={executePurge}
                    disabled={purgeModal.busy || purgeModal.confirm !== purgeModal.preview.confirmation_phrase}
                    className="flex-1 py-2.5 text-sm font-semibold rounded-lg bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="purge-execute-button"
                  >
                    {purgeModal.busy ? "Purging..." : "Permanently Delete"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="edit-ib-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditModal(null)} />
          <div className="card-lux relative w-full max-w-md p-8">
            <p className="eyebrow-label mb-2">Edit IB</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">IB Account Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Name</label>
                <input
                  className="input-lux"
                  value={editModal.full_name}
                  onChange={(e) => setEditModal((m) => ({ ...m, full_name: e.target.value }))}
                  data-testid="edit-ib-fullname-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Email</label>
                <input
                  type="email"
                  className="input-lux"
                  value={editModal.email}
                  onChange={(e) => setEditModal((m) => ({ ...m, email: e.target.value }))}
                  data-testid="edit-ib-email-input"
                />
              </div>
              {modalError && (
                <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="edit-ib-error">
                  {modalError}
                </p>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={saveEdit} disabled={modalBusy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="edit-ib-save-button">
                  {modalBusy ? "Saving..." : "Save"}
                </button>
                <button onClick={() => setEditModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="edit-ib-cancel-button">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {codeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="ib-code-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCodeModal(null)} />
          <div className="card-lux relative w-full max-w-md p-8">
            <p className="eyebrow-label mb-2">Referral Code</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">Set New Referral Code</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Custom Code <span className="text-[#64748B]">(leave empty to auto-generate)</span>
                </label>
                <input
                  className="input-lux font-mono tracking-widest uppercase"
                  placeholder="e.g. SARAH2026"
                  value={codeModal.referral_code}
                  onChange={(e) => setCodeModal((m) => ({ ...m, referral_code: e.target.value.toUpperCase() }))}
                  data-testid="ib-code-input"
                />
              </div>
              {modalError && (
                <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="ib-code-error">
                  {modalError}
                </p>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={saveCode} disabled={modalBusy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="ib-code-save-button">
                  {modalBusy ? "Saving..." : "Set Code"}
                </button>
                <button onClick={() => setCodeModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="ib-code-cancel-button">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
